import { clsx } from 'clsx';
import { forwardRef } from 'react';
import type {
  InputHTMLAttributes,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
  ReactNode,
} from 'react';

// `rounded-full` on single-line controls, paired with roomier side padding so
// the text is not crowded by the curve. Textarea keeps a large-but-finite
// radius: a pill shape on a multi-line box would clip the first and last lines.
// Shared look for every control. The corner radius is deliberately NOT here:
// `clsx` only concatenates, so a `rounded-*` in this string would collide with
// the one each component adds, and Tailwind's output order — not the class
// order in the attribute — would decide the winner. Each control owns its own.
// `text-base` en móvil y `text-sm` a partir de `sm`. No es una preferencia
// tipográfica: Safari de iOS hace zoom automático al enfocar cualquier campo
// cuya letra mida menos de 16px, y al volver deja la página desplazada y más
// ancha que la pantalla. El síntoma se ve como «el desplegable se abre
// gigante». 16px es el umbral exacto; `text-sm` son 14.
const base =
  'w-full border border-line-strong bg-surface-raised text-content ' +
  'placeholder:text-content-subtle transition-colors ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:border-brand-500 ' +
  'disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-content-subtle';

// El tamaño de letra sale de `base` y lo pone cada talla, porque son dos reglas
// distintas y no una con una excepción: en el panel la letra baja a 14 a partir
// de `sm` —la pantalla de trabajo cabe más densa—, y en el formulario público
// se queda en 16 en todos los anchos. Mezclarlas obligaría a anular la primera
// con un `!important`, que es como se acaba con dos reglas peleando.
const tipo = 'text-base sm:text-sm';
const tipoGrande = 'text-base';

// Single-line controls are pills; the roomier side padding keeps the text
// clear of the curve.
const singleLine = 'h-10 rounded-full px-4';

/**
 * La talla del formulario público, y por qué existe una segunda.
 *
 * El panel es una pantalla de trabajo: filas densas, muchos campos a la vista y
 * un ratón. El formulario público lo llena **alguien de pie, con una mano y una
 * pregunta por pantalla**, y ahí un control de 40px con letra de 14 se lee como
 * una casilla de una hoja de cálculo. 48px y letra de 16 en los dos temas —el
 * `sm:text-sm` de la talla normal aquí no aplica: en una laptop este formulario
 * sigue siendo el de un cliente, no el de un operador.
 *
 * **Y cambia la forma, no sólo el tamaño.** La píldora es la firma del panel;
 * el formulario público está construido en tarjetas de radio `2xl` con la
 * sombra apoyada, y el campo es una de ellas.
 *
 * El borde estuvo un tiempo anulado aquí: sobre el fondo teñido de entonces la
 * sombra bastaba para despegar el campo, y un borde se leía como el hueco entre
 * dos tarjetas. Con el fondo en blanco eso dejó de ser cierto — un campo blanco
 * sin borde sobre papel blanco no existe, y lo único que decía «aquí se
 * escribe» era una sombra que en una pantalla con brillo no se ve. Vuelve el
 * borde, más suave que la línea del panel (`line` y no `line-strong`): tiene
 * que encontrarse el campo, no gritar.
 */
const singleLineGrande = 'h-14 rounded-2xl px-4 !border-line shadow-tarjeta';

interface Talla {
  /** La talla del formulario público. Ver `singleLineGrande`. */
  grande?: boolean;
}

export function Input({
  className,
  grande,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & Talla) {
  return (
    <input
      {...rest}
      className={clsx(
        base,
        grande ? [tipoGrande, singleLineGrande] : [tipo, singleLine],
        className,
      )}
    />
  );
}

export function Select({
  className,
  grande,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement> & Talla) {
  return (
    <select
      {...rest}
      className={clsx(
        base,
        grande ? [tipoGrande, singleLineGrande] : [tipo, singleLine],
        'pr-9',
        className,
      )}
    />
  );
}

/**
 * Multi-line variant — imported records carry newline-separated detail.
 *
 * A pill shape here would clip the first and last lines against the curve, so
 * this keeps a large-but-finite radius and pads the text away from the corners.
 */
// Reenvía su `ref`, como `Button`: un diálogo que existe para escribir tiene
// que poder poner el foco en el campo al abrirse, y sin esto no hay a qué
// apuntar. Ver `MotivoDialog`.
export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement> & Talla
>(function Textarea({ className, rows = 4, grande, ...rest }, ref) {
  return (
    <textarea
      {...rest}
      ref={ref}
      rows={rows}
      className={clsx(
        base,
        grande ? tipoGrande : tipo,
        'resize-y rounded-2xl px-4 py-3 leading-relaxed',
        className,
      )}
    />
  );
});

export function Field({
  label,
  hint,
  error,
  required,
  grande,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  /** Marca el campo como obligatorio; sólo pinta la marca, no valida. */
  required?: boolean;
  children: ReactNode;
} & Talla) {
  return (
    <label className={clsx('block', grande ? 'space-y-2' : 'space-y-1')}>
      {/* Dos etiquetas y no una con excepciones: la del panel se queda como
          estaba —en línea, con el asterisco pegado al texto— porque la monta
          cada formulario de la herramienta y no hay nada que ganar moviéndola.
          La grande necesita una fila para colgar «Opcional» al lado. */}
      {grande ? (
        <span className="flex items-baseline gap-2 text-[0.9375rem] font-medium text-content">
          {label}
          {/* El asterisco de siempre, que es lo que la gente busca — pero **no
              en rojo**. Aquí casi todo es obligatorio, y una pantalla con seis
              asteriscos rojos parece que ya se falló en algo antes de escribir
              nada; el rojo hay que dejarlo para cuando de verdad haya un error,
              o cuando llegue no significará nada. En gris marca igual y no
              alarma.

              «Opcional» se queda en los pocos campos que lo son: dicho por su
              nombre no hay que deducirlo de la ausencia de un símbolo, que es
              justo lo que se pasa por alto al llenar deprisa. */}
          {required ? (
            <span aria-hidden="true" className="font-normal text-content-subtle">
              *
            </span>
          ) : (
            <span className="text-xs font-normal text-content-subtle">Opcional</span>
          )}
        </span>
      ) : (
        <span className="text-sm font-medium text-content-muted">
          {label}
          {required && <span className="ml-0.5 text-red-500">*</span>}
        </span>
      )}
      {children}
      {error ? (
        <span className="block text-xs text-red-600 dark:text-red-400">{error}</span>
      ) : hint ? (
        <span
          className={clsx('block text-content-subtle', grande ? 'text-sm' : 'text-xs')}
        >
          {hint}
        </span>
      ) : null}
    </label>
  );
}
